<template>
  <div id="app">
    <main>
      <transition :name="transitionName">
        <router-view @show-modal="showModal($event)" @scroll.native="handleScroll" />
      </transition>
    </main>
    <prt-bottom-bar :visible="$route.name !== 'onboarding' && showNavOverlays"/>
    <prt-add-expense :show="showAddExpense" @close="hideModal"/>
    <prt-search placeholder="Search for transactions" :show="showSearch" @close="hideModal" />
    <prt-fab icon="add" :visible="showOverviewFAB" @click.native="showModal('addExpense')"/>
    <prt-fab icon="search" :visible="showTransactionsFAB" @click.native="showModal('search')" />
    <transition-group class="toast-container" :class="{'no-overlays': !showNavOverlays}" tag="section" name="bubble">
      <prt-toast :action="toast.action" :callback="toast.callback" :color="toast.color" :content="toast.content" :id="toast.id" :key="toast.id" :timeout="toast.timeout" v-for="toast in toasts" />
    </transition-group>
    <prt-content-modal :show="showChangelog" @close="hideModal"><div v-html="changelog" /></prt-content-modal>
    <prt-content-modal :show="showPrivacyPolicy" @close="hideModal">
      <h1>Privacy Policy</h1>
      <p>Prt Mny does not collect any user data, or tracks the user in any way. It doesn’t use cookies. It only stores the data the user inputted on the user’s device. This data can easily be exported from the Settings screen.</p>
      <p>However, Prt Mny is hosted on <a href="https://netlify.com" target="_blank">Netlify</a> in order to be publicly accessible. Netlify stores the IP addresses of site visitors for less than 30 days. For more information, please turn to their <a href="https://www.netlify.com/privacy" target="_blank">Privacy Policy</a> and <a href="https://www.netlify.com/gdpr" target="_blank">GDPR Compliance Statement</a>.</p>
      <p>In addition to that, Prt Mny uses <a href="https://fonts.google.com">Google Web Fonts</a> to ensure a uniform appearance accross devices and operating systems. Requests for CSS assets are cached for 1 day, while the font files are cached for one year. Requests for CSS and font files are logged by Google Fonts. For more information, please see <a href="https://developers.google.com/fonts/faq?hl=en#what_does_using_the_google_fonts_api_mean_for_the_privacy_of_my_users">Google’s answer to privacy with Google Webfonts</a> and <a href="https://www.google.com/policies/privacy/">Google’s Privacy Policy</a>.</p>
    </prt-content-modal>
    <prt-loader-modal :label="loadingModal" :show="loadingModal.length > 0"/>
  </div>
</template>

<script>
import PrtAddExpense from './components/ui/PrtAddExpense.vue';
import PrtBottomBar from './components/ui/PrtBottomBar.vue';
import PrtContentModal from './components/ui/PrtContentModal.vue';
import PrtFab from './components/ui/PrtFab.vue';
import PrtLoaderModal from './components/ui/PrtLoaderModal.vue';
import PrtSearch from './components/ui/PrtSearch.vue';
import PrtToast from './components/ui/PrtToast.vue';

const changelog = require('../CHANGELOG.md');

export default {
  components: {
    PrtAddExpense,
    PrtBottomBar,
    PrtContentModal,
    PrtFab,
    PrtLoaderModal,
    PrtSearch,
    PrtToast,
  },
  computed: {
    installPrompt() {
      return this.$store.state.application.installPrompt;
    },
    forceChangelog() {
      return this.$store.state.user.forceChangelog;
    },
    loadingModal() {
      return this.$store.state.application.loadingModal;
    },
    showOverviewFAB() {
      return this.$route.name === 'overview' && this.toasts.length === 0 && this.showNavOverlays;
    },
    showTransactionsFAB() {
      return this.$route.name === 'transactions' && this.toasts.length === 0 && this.showNavOverlays;
    },
    stateInitialized() {
      return this.$store.state.application.stateInitialized;
    },
    toasts() {
      return this.$store.state.application.toasts;
    },
    userPrompted() {
      return this.$store.state.user.prompted;
    },
  },
  data() {
    return {
      animationFrame: false,
      changelog,
      lastScrollTop: 0,
      transitionName: 'slide-left',
      scrollTicking: false,
      showAddExpense: false,
      showChangelog: false,
      showPrivacyPolicy: false,
      showNavOverlays: true,
      showSearch: false,
    };
  },
  methods: {
    handleScroll(e) {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = window.requestAnimationFrame(() => {
        const scroll = e.target.scrollTop;
        if (scroll <= this.lastScrollTop && !this.showNavOverlays) this.showNavOverlays = true;
        else if (scroll > this.lastScrollTop && this.showNavOverlays) this.showNavOverlays = false;
        this.lastScrollTop = scroll;
        this.scrollTicking = false;
      });
    },
    hideModal() {
      this.$router.go(-1);
    },
    showModal(name) {
      switch (name) {
        case 'addExpense':
          this.showAddExpense = true;
          this.$router.push('#addExpense');
          break;
        case 'changelog':
          this.showChangelog = true;
          this.$router.push('settings#changelog');
          break;
        case 'forceChangelog':
          this.showChangelog = true;
          this.$store.dispatch('setForceChangelog', false);
          this.$router.push('#forceChangelog');
          break;
        case 'privacyPolicy':
          this.showPrivacyPolicy = true;
          this.$router.push('settings#privacyPolicy');
          break;
        case 'search':
          this.showSearch = true;
          this.$router.push('transactions#search');
          break;
        default:
          break;
      }
    },
  },
  name: 'app',
  watch: {
    $route(to, from) {
      // clear toasts
      this.$store.commit('clearToasts');
      // set the correct transition
      if (from.path === '/') this.transitionName = 'slide-right';
      else if (from.path === '/transactions') {
        if (to.path === '/') this.transitionName = 'slide-left';
        else this.transitionName = 'slide-right';
      } else if (from.path === '/settings') this.transitionName = 'slide-left';

      // remove any overlays if there's a hash
      switch (from.hash) {
        case '#addExpense':
          this.showAddExpense = false;
          break;
        case '#changelog':
          this.showChangelog = false;
          break;
        case '#forceChangelog':
          this.showChangelog = false;
          break;
        case '#privacyPolicy':
          this.showPrivacyPolicy = false;
          break;
        case '#search':
          this.showSearch = false;
          break;
        default:
          break;
      }

      // hide nav overlays on onboarding
      if (to.name === 'onboarding') this.showNavOverlays = false;
      if (from.name === 'onboarding' && !this.showNavOverlays) this.showNavOverlays = true;

      // check if there’s an install prompt so we can use it
      if (to.name === 'overview' && from.name !== 'onboarding' && this.installPrompt && !this.userPrompted) {
        this.$store.commit('addToast', {
          content: 'You seem to be interested in using Prt Mny, would you like to install on your device?',
          color: 'positive',
          timeout: -1,
          action: 'Install',
          callback: () => {
            this.installPrompt.prompt();
            this.installPrompt.userChoice
              .then((choice) => {
                if (choice.outcome === 'accepted') this.$store.commit('addToast', { content: 'Great! Thank you for your trust.', color: 'positive', timeout: 5000 });
                else this.$store.commit('addToast', { content: 'Should you change your mind, you can still install the application from the settings.', color: 'positive', timeout: 5000 });
                this.$store.commit('setInstallPrompt', null);
                this.$store.dispatch('setUserPrompted', true);
              });
          },
        });
      }
    },
    stateInitialized(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.forceChangelog) this.showModal('forceChangelog');
        });
      }
    },
  },
};
</script>

<style lang="stylus">
  @require 'styles/base'
  @require 'styles/colors'

  .toast-container
    position: fixed
    bottom: 56px
    left: 0
    width: 100%
    z-index: 99

    &.no-overlays
      bottom: 0

    .bubble-enter-active,
    .bubble-move,
    .bubble-leave-active
      transition: transform 200ms ease, opacity 200ms ease

    .bubble-leave-active
      position: absolute

    .bubble-enter,
    .bubble-leave-to
      transform: translateY(100%)
      opacity: 0

  .slide-left-enter-active,
  .slide-right-enter-active,
  .slide-left-leave-active,
  .slide-right-leave-active
    transition: transform 200ms ease
    position: fixed
    width: 100vw

  .slide-left-enter,
  .slide-right-leave-to
    transform: translateX(-100vw)

  .slide-right-enter,
  .slide-left-leave-to
    transform: translateX(100vw)

</style>
